import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, pipe, UnaryFunction } from 'rxjs';
import { tap } from 'rxjs/operators';
import { readFirst } from '@dg/shared-rxjs';

import { AuthService } from '@app/shared/services/auth.service';
import { WindowLayoutService } from '@app/shared/services/window-layout/window-layout.service';
import { SearchFiltersStateService } from '@app/search/components/search-filters/search-filters-state.service';
import { deleteUndefinedOrNull } from '@app/shared/utils';

import { SearchFacade } from './search.facade';
import {
  QueryUrlParams,
  SearchState,
  SearchViewModel,
} from './models/search.model';
import {
  stateToUrlParams,
  urlParamsToState,
  coerceUrlParams,
  stripDefaultParams,
  verifyUrlParams,
} from './utils';

/**
 * The UrlSync for search enables bi-directional synchronization between
 * url query params and search 'state'.
 *
 * The UrlSync class decouples
 *   - UI components from URL synchronization details
 *   - SearchFacade from Router details
 */
@Injectable()
export class SearchBookmarkService {
  /**
   * Create a 'rxjs' operator to tap SearchViewModel emissions and update the
   * url query params.
   */
  public updateUrl: UnaryFunction<
    Observable<SearchViewModel>,
    Observable<SearchViewModel>
  >;

  constructor(
    private facade: SearchFacade,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private windowLayoutService: WindowLayoutService,
    private searchFiltersStateService: SearchFiltersStateService
  ) {
    // Build operator that taps a SearchViewModel stream
    this.updateUrl = pipe(tap(this.updateUrlFrom.bind(this)));
  }

  /**
   * Initial URL -> Search State
   *
   * This is a 'bookmark' feature that will use the current url query params and
   * update the SearchState to match.
   *
   * Note: This only runs once when the search results component first inits
   * and we fall back to the Search State initial values if there are
   * no overrides in the URL
   */
  public updateState() {
    verifyUrlParams(this.route.snapshot.queryParams);

    const params = coerceUrlParams(this.route.snapshot.queryParams);
    const { orgId, isExternalCat, isMarketplaceCat } = params;
    const defaultOrgId = this.authService.authUser?.defaultOrgId;

    // Fall back to the default org id if it isn't already set
    // in the URL and we're not viewing the external catalog
    if (!orgId && !isExternalCat && !isMarketplaceCat) {
      params.orgId = defaultOrgId;
    }

    // Keep the search filters state in sync
    if (Boolean(params.ignorePreferredLanguage)) {
      this.searchFiltersStateService.setIgnorePreferredLanguage(true);
    }

    const state = urlParamsToState(deleteUndefinedOrNull(params));

    // We're initializing so reset store, run spell check on search term and
    // tell the back end to cache the filter state
    const resetStore = true;
    const spellcheckSearchTerm = true;
    const persistFilter = true;

    const search$ = this.facade.searchLearnings(
      { ...state, defaultOrgId },
      resetStore,
      spellcheckSearchTerm,
      persistFilter
    );

    readFirst(search$);
  }

  /**
   * Search State updates -> URL
   *
   * Update the url with the given state changes
   */
  private updateUrlFrom(state: SearchState) {
    const params = this.buildQueryParams(state);

    if (params) {
      const { ignorePreferredLanguage } = this.searchFiltersStateService;
      const is_msteams = this.windowLayoutService.isMsTeams;
      const queryParams = stripDefaultParams({
        ...params,
        ignorePreferredLanguage,
        is_msteams,
      });

      verifyUrlParams(queryParams);

      this.router.navigate([], {
        queryParams,
        relativeTo: this.route,
        replaceUrl: true,
      });
    }
  }

  /**
   * Map SearchState to specific parameters we wish to expose/publish on URL
   */
  private buildQueryParams(state: SearchState): Partial<QueryUrlParams> {
    const queryParams = stateToUrlParams(state);

    // A pageNum of "0" indicates our state hasn't been fully initialized
    // so we wait until it's updated before building...
    if (queryParams.pageNum > 0) {
      return deleteUndefinedOrNull<Partial<QueryUrlParams>>(queryParams);
    }

    return undefined;
  }
}
